import React, { useState } from "react";
import { useQuery, useMutation, gql } from "@apollo/client";

// Check before locking: what payroll would pay each professional this
// month, deductions explained line by line, with flags on the cases worth
// a second look. Opened from a run's "Check" / "Lock Run"; when `onLock` is
// passed the footer offers locking once the admin has looked.

const GET_PAYROLL_CHECK = gql`
  query GetPayrollCheck($month: Int!, $year: Int!) {
    getPayrollCheck(month: $month, year: $year) {
      status
      message
      check {
        month
        year
        runStatus
        people
        flaggedPeople
        totalNet
        totalLeaveDeduction
        totalFinesOverCap
        unverifiedBookings
        monthOver
        pendingApprovals
        settledInFnf
        makerChecker
        lockRequestedBy
        flagCounts {
          code
          count
        }
        rows {
          employeeId
          fullName
          salaryType
          activeDays
          daysInMonth
          baseSalary
          commission
          bookings
          leaveDayCharges
          leaveFines
          finesOverCap
          leaveDeduction
          adjustmentTotal
          netPayout
          draftNetPayout
          deductionPercent
          unverifiedBookings
          flags {
            code
            message
          }
          fines {
            id
            date
            amount
            dayCharge
            reason
            waived
            fineWaived
          }
          adjustments {
            id
            type
            credit
            amount
            description
            date
          }
        }
      }
    }
  }
`;

const REQUEST_LOCK = gql`
  mutation RequestPayrollLock($payrollRunId: ID!) {
    requestPayrollLock(payrollRunId: $payrollRunId) {
      status
      message
    }
  }
`;
const APPROVE_LOCK = gql`
  mutation LockPayroll($payrollRunId: ID!) {
    lockPayroll(payrollRunId: $payrollRunId) {
      status
      message
    }
  }
`;
const WITHDRAW_LOCK = gql`
  mutation CancelPayrollLockRequest($payrollRunId: ID!) {
    cancelPayrollLockRequest(payrollRunId: $payrollRunId) {
      status
      message
    }
  }
`;

const FLAG_INFO = {
  PENDING_APPROVALS: { label: "Waiting for approval", tone: "red" },
  NOT_IN_PAYROLL: { label: "Not paid at all", tone: "red" },
  UNVERIFIED_BOOKINGS: { label: "Unverified bookings", tone: "red" },
  LEFT_THIS_MONTH: { label: "Left this month", tone: "gray" },
  NET_ZERO: { label: "Net ₹0", tone: "red" },
  DRAFT_OUT_OF_DATE: { label: "Draft out of date", tone: "red" },
  NOT_IN_DRAFT: { label: "Missing from draft", tone: "red" },
  OPEN_DISPUTES: { label: "Open question", tone: "amber" },
  PENDING_LEAVES: { label: "Pending leave", tone: "amber" },
  HIGH_DEDUCTIONS: { label: "High deductions", tone: "amber" },
  FINE_CAP_REACHED: { label: "Fine cap reached", tone: "blue" },
  NEW_JOINER_FINED: { label: "Fined in probation", tone: "amber" },
  NO_SALARY_SET: { label: "No salary", tone: "amber" },
  NO_JOINING_DATE: { label: "No joining date", tone: "gray" },
};
const TONE = {
  red: "bg-red-100 text-red-800",
  amber: "bg-yellow-100 text-yellow-800",
  blue: "bg-blue-100 text-blue-800",
  gray: "bg-gray-100 text-gray-700",
};
// Flags that mean the numbers about to be locked are wrong or unsettled
const BLOCKING = ["PENDING_APPROVALS", "NOT_IN_PAYROLL", "UNVERIFIED_BOOKINGS", "NET_ZERO", "DRAFT_OUT_OF_DATE", "NOT_IN_DRAFT", "OPEN_DISPUTES", "PENDING_LEAVES"];

const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString("en-IN")}`;
const monthName = (m, y) =>
  new Date(y, m - 1, 1).toLocaleString("en-IN", { month: "long", year: "numeric" });

const FlagChip = ({ code }) => {
  const info = FLAG_INFO[code] || { label: code, tone: "gray" };
  return (
    <span className={`px-2 py-0.5 rounded text-[11px] font-semibold whitespace-nowrap ${TONE[info.tone]}`}>
      {info.label}
    </span>
  );
};

const PersonDetail = ({ row }) => (
  <div className="bg-gray-50 px-4 py-3 text-xs text-gray-700 space-y-3">
    {row.flags.length > 0 && (
      <ul className="space-y-1">
        {row.flags.map((f, i) => (
          <li key={i} className="flex gap-2 items-start">
            <FlagChip code={f.code} />
            <span>{f.message}</span>
          </li>
        ))}
      </ul>
    )}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <div className="font-semibold text-gray-800 mb-1">Earnings</div>
        <div className="flex justify-between">
          <span>
            Salary ({row.activeDays} of {row.daysInMonth} days{row.salaryType ? `, ${row.salaryType}` : ""})
          </span>
          <span>{inr(row.baseSalary)}</span>
        </div>
        <div className="flex justify-between">
          <span>Commission ({row.bookings} bookings)</span>
          <span>{inr(row.commission)}</span>
        </div>
        {row.adjustments.length > 0 && (
          <>
            <div className="font-semibold text-gray-800 mt-3 mb-1">Adjustments</div>
            {row.adjustments.map((a) => (
              <div key={a.id} className="flex justify-between gap-2">
                <span className="truncate" title={a.description || ""}>
                  {a.date} · {a.type}
                  {a.description ? ` · ${a.description}` : ""}
                </span>
                <span className={a.credit ? "text-green-700" : "text-red-700"}>
                  {a.credit ? "+" : "-"}
                  {inr(a.amount)}
                </span>
              </div>
            ))}
          </>
        )}
      </div>
      <div>
        <div className="font-semibold text-gray-800 mb-1">
          Leave deductions {inr(row.leaveDeduction)}
          <span className="font-normal text-gray-500">
            {" "}
            = day charges {inr(row.leaveDayCharges)} + fines {inr(row.leaveFines)}
            {row.finesOverCap > 0 && ` - over cap ${inr(row.finesOverCap)}`}
          </span>
        </div>
        {row.fines.length === 0 && <div className="text-gray-400">No leave days this month</div>}
        {row.fines.map((f) => (
          <div key={f.id} className={`flex justify-between gap-2 ${f.waived ? "text-gray-400 line-through" : ""}`}>
            <span className="truncate" title={f.reason || ""}>
              {f.date} · {f.reason}
              {f.fineWaived && <span className="text-yellow-700"> · fine waived</span>}
            </span>
            <span className="whitespace-nowrap">
              {inr(f.amount)}
              {f.dayCharge ? <span className="text-gray-400"> (day {inr(f.dayCharge)})</span> : null}
            </span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// `runId` + `canLock`: opened from "Check & Lock" - the footer offers the
// lock (with maker-checker: request it, or approve someone else's request)
const PayrollCheckModal = ({ month, year, onClose, runId, canLock, onChanged }) => {
  const [requestLock] = useMutation(REQUEST_LOCK);
  const [approveLock] = useMutation(APPROVE_LOCK);
  const [withdrawLock] = useMutation(WITHDRAW_LOCK);
  const { data, loading, refetch } = useQuery(GET_PAYROLL_CHECK, {
    variables: { month, year },
    fetchPolicy: "network-only",
  });
  const [open, setOpen] = useState({});
  const [onlyFlagged, setOnlyFlagged] = useState(true);
  const res = data?.getPayrollCheck;
  const check = res?.check;
  const rows = (check?.rows || []).filter((r) => !onlyFlagged || r.flags.length);
  const blocking = (check?.rows || []).filter((r) => r.flags.some((f) => BLOCKING.includes(f.code)));

  const lock = () => {
    const unverified = check.unverifiedBookings
      ? `${check.unverifiedBookings} bookings of this month are not verified yet - they will get NO commission after locking. `
      : "";
    const msg = blocking.length
      ? `${unverified}${blocking.length} people have unsettled pay (unverified bookings, open questions, pending leaves, an out-of-date draft or not in payroll). Lock anyway? Locked payouts can't be changed.`
      : "Lock this payroll? Payout totals become final and visible to professionals.";
    if (!window.confirm(msg)) return;
    run(check.makerChecker && check.lockRequestedBy ? approveLock : requestLock, check.makerChecker && !check.lockRequestedBy);
  };

  // keepOpen: after a lock request the check stays open, showing who asked
  const run = async (mutation, keepOpen) => {
    try {
      const res = await mutation({ variables: { payrollRunId: runId } });
      const out = Object.values(res.data)[0];
      alert(out.message);
      if (out.status === "SUCCESS") {
        if (onChanged) onChanged();
        if (keepOpen) refetch();
        else onClose();
      }
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl p-6 max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">
              Payroll check · {monthName(month, year)}
            </h2>
            <p className="text-xs text-gray-500">
              What payroll would pay today, computed the same way as the run. Nothing changes here.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800 text-xl leading-none">
            ×
          </button>
        </div>

        {loading ? (
          <p className="text-sm text-gray-500">Checking every professional...</p>
        ) : res?.status === "ERROR" ? (
          <p className="text-sm text-red-600">{res.message}</p>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-sm">
              <div className="border rounded p-3">
                <div className="text-xs text-gray-500">People</div>
                <div className="font-semibold">{check.people}</div>
              </div>
              <div className={`border rounded p-3 ${check.flaggedPeople ? "border-yellow-300 bg-yellow-50" : ""}`}>
                <div className="text-xs text-gray-500">To look at</div>
                <div className="font-semibold">{check.flaggedPeople}</div>
              </div>
              <div className="border rounded p-3">
                <div className="text-xs text-gray-500">Leave deductions</div>
                <div className="font-semibold">{inr(check.totalLeaveDeduction)}</div>
                {check.totalFinesOverCap > 0 && (
                  <div className="text-[11px] text-blue-700">{inr(check.totalFinesOverCap)} kept off by the cap</div>
                )}
              </div>
              <div className="border rounded p-3">
                <div className="text-xs text-gray-500">Total net pay</div>
                <div className="font-semibold">{inr(check.totalNet)}</div>
              </div>
            </div>

            {check.unverifiedBookings > 0 && (
              <div className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                <b>{check.unverifiedBookings}</b> bookings of this month are not verified yet. Verify them in Bookings
                before locking - a booking verified after the lock earns no commission.
              </div>
            )}
            {check.pendingApprovals > 0 && (
              <div className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                <b>{check.pendingApprovals}</b> adjustments of this month are waiting for approval and are not counted
                yet. Approve or reject them in Staff Desk → Approvals before locking.
              </div>
            )}
            {check.makerChecker && check.lockRequestedBy && check.runStatus === "DRAFT" && (
              <div className="mb-3 rounded border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">
                Lock requested by <b>{check.lockRequestedBy}</b>. Another admin approves it.
              </div>
            )}
            {check.settledInFnf > 0 && (
              <div className="mb-3 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                {check.settledInFnf} professional(s) were paid for this month in their full &amp; final and are left out.
              </div>
            )}
            {!check.monthOver && (
              <div className="mb-3 rounded border border-yellow-200 bg-yellow-50 px-3 py-2 text-sm text-yellow-800">
                This month isn't over yet, so it can't be locked. The numbers below are what payroll would pay so far.
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2 mb-3">
              {check.flagCounts.map((c) => (
                <span key={c.code} className="flex items-center gap-1">
                  <FlagChip code={c.code} />
                  <span className="text-xs text-gray-600">{c.count}</span>
                </span>
              ))}
              <label className="ml-auto flex items-center gap-2 text-xs text-gray-600">
                <input type="checkbox" checked={onlyFlagged} onChange={(e) => setOnlyFlagged(e.target.checked)} />
                Only people with flags
              </label>
            </div>

            <div className="overflow-y-auto border rounded flex-1">
              <table className="min-w-full text-left text-sm text-gray-600">
                <thead className="bg-gray-50 text-gray-700 border-b sticky top-0">
                  <tr>
                    <th className="py-2 px-3">Professional</th>
                    <th className="py-2 px-3 text-right">Earned</th>
                    <th className="py-2 px-3 text-right">Leave</th>
                    <th className="py-2 px-3 text-right">Adjust.</th>
                    <th className="py-2 px-3 text-right">Net</th>
                    <th className="py-2 px-3">Flags</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={6} className="py-6 text-center text-gray-400">
                        Nothing flagged.
                      </td>
                    </tr>
                  )}
                  {rows.map((r) => (
                    <React.Fragment key={r.employeeId}>
                      <tr
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => setOpen((o) => ({ ...o, [r.employeeId]: !o[r.employeeId] }))}
                      >
                        <td className="py-2 px-3 text-gray-800">
                          <span className="text-gray-400 mr-1">{open[r.employeeId] ? "▾" : "▸"}</span>
                          {r.fullName}
                        </td>
                        <td className="py-2 px-3 text-right">{inr(r.baseSalary + r.commission)}</td>
                        <td className="py-2 px-3 text-right text-red-700">
                          -{inr(r.leaveDeduction)}
                          {r.deductionPercent !== null && (
                            <span className="text-[11px] text-gray-400"> {r.deductionPercent}%</span>
                          )}
                        </td>
                        <td className="py-2 px-3 text-right">{inr(r.adjustmentTotal)}</td>
                        <td className="py-2 px-3 text-right font-semibold text-gray-800">
                          {inr(r.netPayout)}
                          {r.draftNetPayout !== null && r.draftNetPayout !== r.netPayout && (
                            <div className="text-[11px] font-normal text-red-600">draft {inr(r.draftNetPayout)}</div>
                          )}
                        </td>
                        <td className="py-2 px-3">
                          <div className="flex flex-wrap gap-1">
                            {r.flags.map((f, i) => (
                              <FlagChip key={i} code={f.code} />
                            ))}
                          </div>
                        </td>
                      </tr>
                      {open[r.employeeId] && (
                        <tr>
                          <td colSpan={6} className="p-0">
                            <PersonDetail row={r} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-between items-center mt-4">
              <button onClick={() => refetch()} className="text-sm text-gray-600 hover:text-gray-900 font-semibold">
                Check again
              </button>
              <div className="flex gap-2">
                <button onClick={onClose} className="px-4 py-2 rounded border text-sm font-semibold text-gray-700">
                  Close
                </button>
                {canLock && check.runStatus === "DRAFT" && check.monthOver && check.lockRequestedBy && (
                  <button
                    onClick={() => run(withdrawLock, true)}
                    className="px-4 py-2 rounded border text-sm font-semibold text-gray-700"
                  >
                    Withdraw request
                  </button>
                )}
                {canLock && check.runStatus === "DRAFT" && check.monthOver && (
                  <button
                    onClick={lock}
                    className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold"
                    title={check.makerChecker ? "One admin requests the lock, another approves it" : ""}
                  >
                    {!check.makerChecker ? "Lock now" : check.lockRequestedBy ? "Approve & lock" : "Request lock"}
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PayrollCheckModal;
